import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Skeleton,
  Alert,
  Tabs,
  Tab,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import {
  Videocam as StartIcon,
  Schedule as ScheduleIcon,
  List as ListIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import api from "../../api";
import JitsiMeet from "../../components/JitsiMeet";

const statusConfig = {
  pending: { label: "Pending", color: "#ff9800" },
  ongoing: { label: "Ongoing", color: "#4caf50" },
  ended: { label: "Ended", color: "#9e9e9e" },
};

const formatDateTime = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
};

/** Jitsi Meet URL with display name pre-filled (userInfo.displayName hash). */
const getJitsiMeetingUrl = (roomCode, displayName) => {
  const base = `https://meet.jit.si/${roomCode}`;
  if (!displayName || !displayName.trim()) return base;
  const encoded = encodeURIComponent(JSON.stringify(displayName.trim()));
  return `${base}#userInfo.displayName=${encoded}`;
};

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

export default function TutorClasses() {
  const [courses, setCourses] = useState([]);
  const [classrooms, setClassrooms] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [loadingClassrooms, setLoadingClassrooms] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [tab, setTab] = useState(0);
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get("tab");
    if (tabParam !== null) {
      setTab(Number(tabParam));
    }
  }, [location.search]);

  // Start class
  const [startCourseId, setStartCourseId] = useState("");
  const [startSubjectId, setStartSubjectId] = useState("");
  const [startSubjects, setStartSubjects] = useState([]);
  const [loadingStartSubjects, setLoadingStartSubjects] = useState(false);
  const [creating, setCreating] = useState(false);

  // Schedule class
  const [scheduleCourseId, setScheduleCourseId] = useState("");
  const [scheduleSubjectId, setScheduleSubjectId] = useState("");
  const [scheduleSubjects, setScheduleSubjects] = useState([]);
  const [loadingScheduleSubjects, setLoadingScheduleSubjects] = useState(false);
  const [scheduleName, setScheduleName] = useState("");
  const [scheduleStart, setScheduleStart] = useState("");
  const [scheduleEnd, setScheduleEnd] = useState("");
  const [scheduling, setScheduling] = useState(false);
  const [endingMeetingId, setEndingMeetingId] = useState(null);
  const [startingMeetingId, setStartingMeetingId] = useState(null);
  const [classFilter, setClassFilter] = useState("all"); // all | pending | ongoing | ended

  const tutorName = localStorage.getItem("tutor_name") || "Tutor";

  const statusOrder = { pending: 0, ongoing: 1, ended: 2 };
  const sortedClassrooms = [...classrooms].sort((a, b) => {
    const oa = statusOrder[a.status] ?? 3;
    const ob = statusOrder[b.status] ?? 3;
    if (oa !== ob) return oa - ob;
    return (a.start_time || "").localeCompare(b.start_time || "");
  });
  const filteredClassrooms = classFilter === "all"
    ? sortedClassrooms
    : sortedClassrooms.filter((c) => c.status === classFilter);

  const fetchCourses = async () => {
    setLoadingCourses(true);
    try {
      const res = await api.get("tutor/courses");
      if (res.data?.success && Array.isArray(res.data.data)) {
        setCourses(res.data.data);
        if (res.data.data.length && !startCourseId) setStartCourseId(String(res.data.data[0].id));
        if (res.data.data.length && !scheduleCourseId) setScheduleCourseId(String(res.data.data[0].id));
      }
    } catch (e) {
      setError(e.response?.data?.message || "Failed to load courses");
    } finally {
      setLoadingCourses(false);
    }
  };

  const fetchClassrooms = async () => {
    setLoadingClassrooms(true);
    try {
      const res = await api.get("tutor/classrooms");
      if (res.data?.success && Array.isArray(res.data.data)) {
        setClassrooms(res.data.data);
      }
    } catch (e) {
      setError(e.response?.data?.message || "Failed to load classes");
    } finally {
      setLoadingClassrooms(false);
    }
  };

  useEffect(() => {
    fetchCourses();
  }, []);

  // Load available subjects for the selected course (start meeting tab).
  useEffect(() => {
    if (!startCourseId) return;

    let cancelled = false;
    setStartSubjectId("");
    setStartSubjects([]);
    setLoadingStartSubjects(true);

    api
      .get(`tutor/courses/${encodeURIComponent(startCourseId)}/subjects`)
      .then((res) => {
        if (cancelled) return;
        const list = res.data?.success && Array.isArray(res.data.data) ? res.data.data : [];
        setStartSubjects(list);
        if (list.length) setStartSubjectId(String(list[0].id));
      })
      .catch(() => {
        if (cancelled) return;
        setStartSubjects([]);
        setStartSubjectId("");
      })
      .finally(() => {
        if (cancelled) return;
        setLoadingStartSubjects(false);
      });

    return () => {
      cancelled = true;
    };
  }, [startCourseId]);

  // Load available subjects for the selected course (schedule meeting tab).
  useEffect(() => {
    if (!scheduleCourseId) return;

    let cancelled = false;
    setScheduleSubjectId("");
    setScheduleSubjects([]);
    setLoadingScheduleSubjects(true);

    api
      .get(`tutor/courses/${encodeURIComponent(scheduleCourseId)}/subjects`)
      .then((res) => {
        if (cancelled) return;
        const list = res.data?.success && Array.isArray(res.data.data) ? res.data.data : [];
        setScheduleSubjects(list);
        if (list.length) setScheduleSubjectId(String(list[0].id));
      })
      .catch(() => {
        if (cancelled) return;
        setScheduleSubjects([]);
        setScheduleSubjectId("");
      })
      .finally(() => {
        if (cancelled) return;
        setLoadingScheduleSubjects(false);
      });

    return () => {
      cancelled = true;
    };
  }, [scheduleCourseId]);

  useEffect(() => {
    if (tab === 2) fetchClassrooms();
  }, [tab]);

  const handleStartMeeting = async () => {
    if (!startCourseId || !startSubjectId) {
      setError("Select a course and subject.");
      return;
    }
    const course = courses.find((c) => String(c.id) === String(startCourseId));
    const subject = startSubjects.find((s) => String(s.id) === String(startSubjectId));
    setCreating(true);
    setError(null);
    try {
      const res = await api.post("tutor/classrooms", {
        course_id: Number(startCourseId),
        subject_id: Number(startSubjectId),
        name: course && subject ? `${course.name} - ${subject.name}` : "Live class",
      });
      const roomCode = res.data?.classroom?.room_code;
      if (roomCode) {
        window.open(getJitsiMeetingUrl(roomCode, tutorName), "_blank", "noopener,noreferrer");
        setSuccess("Meeting opened in a new tab. Students can join via the Classes section.");
      }
    } catch (e) {
      setError(e.response?.data?.message || "Failed to create meeting");
    } finally {
      setCreating(false);
    }
  };

  const handleScheduleClass = async (e) => {
    e.preventDefault();
    if (!scheduleCourseId || !scheduleSubjectId || !scheduleName.trim()) {
      setError("Course, subject, and class name are required.");
      return;
    }
    setScheduling(true);
    setError(null);
    setSuccess(null);
    try {
      // Send times in UTC so backend stores correctly; datetime-local gives local time without timezone
      const startUtc = scheduleStart ? new Date(scheduleStart).toISOString() : null;
      const endUtc = scheduleEnd ? new Date(scheduleEnd).toISOString() : null;
      await api.post("tutor/classrooms", {
        course_id: Number(scheduleCourseId),
        subject_id: Number(scheduleSubjectId),
        name: scheduleName.trim(),
        start_time: startUtc,
        end_time: endUtc,
      });
      setSuccess("Class scheduled.");
      setScheduleName("");
      setScheduleStart("");
      setScheduleEnd("");
      fetchClassrooms();
    } catch (e) {
      setError(e.response?.data?.message || "Failed to schedule class");
    } finally {
      setScheduling(false);
    }
  };

  const handleStartExisting = async (classroom) => {
    setStartingMeetingId(classroom.id);
    setError(null);
    try {
      await api.post(`tutor/classrooms/${classroom.id}/start`);
      window.open(getJitsiMeetingUrl(classroom.room_code, tutorName), "_blank", "noopener,noreferrer");
      fetchClassrooms();
    } catch (e) {
      setError(e.response?.data?.message || "Failed to start meeting");
    } finally {
      setStartingMeetingId(null);
    }
  };

  const handleEndMeeting = async (classroomId) => {
    setEndingMeetingId(classroomId);
    setError(null);
    try {
      await api.post(`tutor/classrooms/${classroomId}/end`);
      setSuccess("Meeting ended. End time recorded.");
      fetchClassrooms();
    } catch (e) {
      setError(e.response?.data?.message || "Failed to end meeting");
    } finally {
      setEndingMeetingId(null);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
        Classes
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        Start a live meeting, schedule a class, or view and start from your scheduled classes.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: "divider", mb: 0 }}>
        <Tab icon={<StartIcon />} iconPosition="start" label="Start class" />
        <Tab icon={<ScheduleIcon />} iconPosition="start" label="Schedule class" />
        <Tab icon={<ListIcon />} iconPosition="start" label="My scheduled classes" />
      </Tabs>

      <TabPanel value={tab} index={0}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
              Create and start a meeting now
            </Typography>
            <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel>Course</InputLabel>
                <Select
                  value={startCourseId}
                  label="Course"
                  onChange={(e) => setStartCourseId(e.target.value)}
                  disabled={loadingCourses}
                >
                  {courses.map((c) => (
                    <MenuItem key={c.id} value={String(c.id)}>{c.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 220 }} required>
                <InputLabel>Subject</InputLabel>
                <Select
                  value={startSubjectId}
                  label="Subject"
                  onChange={(e) => setStartSubjectId(e.target.value)}
                  disabled={loadingStartSubjects || startSubjects.length === 0}
                >
                  {startSubjects.map((s) => (
                    <MenuItem key={s.id} value={String(s.id)}>{s.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                startIcon={<PlayIcon />}
                onClick={handleStartMeeting}
                disabled={creating || loadingCourses || loadingStartSubjects}
                sx={{ background: "linear-gradient(90deg, #3B2A9F 0%, #D62926 100%)", "&:hover": { opacity: 0.9 } }}
              >
                {creating ? "Creating…" : "Create & start meeting"}
              </Button>
            </Box>
          </CardContent>
        </Card>
        <Typography variant="body2" color="text.secondary">
          Meeting will open in a new tab when you click &quot;Create &amp; start meeting&quot;.
        </Typography>
      </TabPanel>

      <TabPanel value={tab} index={1}>
        <Card>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
              Schedule a class with start and end time
            </Typography>
            <form onSubmit={handleScheduleClass}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 480 }}>
                <FormControl size="small" required>
                  <InputLabel>Course</InputLabel>
                  <Select
                    value={scheduleCourseId}
                    label="Course"
                    onChange={(e) => setScheduleCourseId(e.target.value)}
                    disabled={loadingCourses}
                  >
                    {courses.map((c) => (
                      <MenuItem key={c.id} value={String(c.id)}>{c.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" required>
                  <InputLabel>Subject</InputLabel>
                  <Select
                    value={scheduleSubjectId}
                    label="Subject"
                    onChange={(e) => setScheduleSubjectId(e.target.value)}
                    disabled={loadingScheduleSubjects || scheduleSubjects.length === 0}
                  >
                    {scheduleSubjects.map((s) => (
                      <MenuItem key={s.id} value={String(s.id)}>{s.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  label="Class name"
                  value={scheduleName}
                  onChange={(e) => setScheduleName(e.target.value)}
                  required
                  placeholder="e.g. Week 3 - Algebra"
                />
                <TextField
                  size="small"
                  label="Start time"
                  type="datetime-local"
                  value={scheduleStart}
                  onChange={(e) => setScheduleStart(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  size="small"
                  label="End time"
                  type="datetime-local"
                  value={scheduleEnd}
                  onChange={(e) => setScheduleEnd(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  disabled={scheduling || loadingCourses || loadingScheduleSubjects}
                  sx={{ alignSelf: "flex-start", background: "linear-gradient(90deg, #3B2A9F 0%, #D62926 100%)", "&:hover": { opacity: 0.9 } }}
                >
                  {scheduling ? "Scheduling…" : "Schedule class"}
                </Button>
              </Box>
            </form>
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tab} index={2}>
        {loadingClassrooms ? (
          <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
        ) : classrooms.length === 0 ? (
          <Card><CardContent><Typography color="text.secondary">No scheduled classes yet. Create one from "Start class" or "Schedule class".</Typography></CardContent></Card>
        ) : (
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2, flexWrap: "wrap" }}>
              <ToggleButtonGroup
                value={classFilter}
                exclusive
                onChange={(_, v) => v != null && setClassFilter(v)}
                size="small"
                sx={{
                  "& .MuiToggleButton-root": {
                    textTransform: "none",
                    fontWeight: 600,
                  },
                }}
              >
                <ToggleButton value="all">All</ToggleButton>
                <ToggleButton value="pending">Pending</ToggleButton>
                <ToggleButton value="ongoing">Ongoing</ToggleButton>
                <ToggleButton value="ended">Ended</ToggleButton>
              </ToggleButtonGroup>
              <Button
                startIcon={<RefreshIcon />}
                onClick={fetchClassrooms}
                disabled={loadingClassrooms}
                size="small"
                variant="outlined"
              >
                Refresh
              </Button>
            </Box>
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "grey.100" }}>
                    <TableCell><strong>Class</strong></TableCell>
                    <TableCell><strong>Course</strong></TableCell>
                    <TableCell><strong>Subject</strong></TableCell>
                    <TableCell><strong>Start</strong></TableCell>
                    <TableCell><strong>End</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell align="right"><strong>Action</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredClassrooms.map((c) => {
                  const config = statusConfig[c.status] || statusConfig.pending;
                  return (
                    <TableRow key={c.id}>
                      <TableCell>{c.name}</TableCell>
                      <TableCell>{c.course?.name ?? "—"}</TableCell>
                      <TableCell>{c.subject?.name ?? "—"}</TableCell>
                      <TableCell>{formatDateTime(c.start_time)}</TableCell>
                      <TableCell>{formatDateTime(c.end_time)}</TableCell>
                      <TableCell><Chip size="small" label={config.label} sx={{ bgcolor: config.color + "20", color: config.color }} /></TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", flexWrap: "wrap" }}>
                          {(c.status === "pending" || c.status === "ongoing") && (
                            <Button
                              size="small"
                              variant="contained"
                              startIcon={<PlayIcon />}
                              onClick={() => handleStartExisting(c)}
                              disabled={startingMeetingId === c.id}
                              sx={{ background: "linear-gradient(90deg, #3B2A9F 0%, #D62926 100%)", "&:hover": { opacity: 0.9 } }}
                            >
                              {startingMeetingId === c.id ? "Starting…" : "Start"}
                            </Button>
                          )}
                          {c.status === "ongoing" && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<StopIcon />}
                              onClick={() => handleEndMeeting(c.id)}
                              disabled={endingMeetingId === c.id}
                              sx={{ borderColor: "#d32f2f", color: "#d32f2f", "&:hover": { borderColor: "#b71c1c", bgcolor: "rgba(211,47,47,0.04)" } }}
                            >
                              {endingMeetingId === c.id ? "Ending…" : "End meeting"}
                            </Button>
                          )}
                          {c.status === "ended" && (
                            <Typography variant="body2" color="text.secondary">—</Typography>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            {filteredClassrooms.length === 0 && classFilter !== "all" && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                No {classFilter} classes.
              </Typography>
            )}
          </>
        )}
      </TabPanel>
    </Box>
  );
}
